/**
 * 계약자 정보 상세 컴포넌트
 * 계약자의 기본 정보를 표시하는 컴포넌트입니다.
 * 신청자명, 휴대폰, 이메일, 회원번호, 신청 기간 등의 정보를 보여줍니다.
 */

import { useEffect, useState } from "react";
import { ContractApplicationModel, DetailTotalData } from "src/api/contract/contract-types";
import { ApplicationFormModel } from "src/api/product/product-types";
import { BaseButton, BaseTooltip } from "src/components";
import { ArticleSection } from "src/components/layout/ArticleSection";
import useUsageApplication, { ProductApplication } from "src/pages/product/hooks/useUsageApplication";
import { useModal } from "src/recoil/modalState/hook";
import { formatDateTimeRange, formatPhoneNumber } from "src/utils";
import BaseSection from "../../../../components/layout/BaseSection";
import UsageApplicationDetail from "../../form/components/application/component/UsageApplicationDetail";
import UsageApplicationForm from "../../form/components/application/component/UsageApplicationForm";

/**
 * 컴포넌트 props 인터페이스
 */
interface ContractBasicProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
  contractApplication?: ContractApplicationModel | null; // 계약 이용신청서 정보
  getContractApplication?: () => Promise<void>; // 계약 이용신청서 정보 조회 함수
}

/**
 * 계약자 정보 상세 컴포넌트
 * @param detailData 계약 상세 데이터
 */
const ContractorInfoDetail = ({ detailData, contractApplication, getContractApplication }: ContractBasicProp) => {
  const { contract } = detailData;
  const { setBaseModal } = useModal();

  const { setAbstractModalZ1, setAbstractModalZ2 } = useModal();

  const { getApplicationSummury, onUpdateContractApplication } = useUsageApplication();

  // 계약 신청서 수정
  const onUpdateCtApplication = (formData: ApplicationFormModel) => {
    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      onClick: () => {
        execute();
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
    });

    const execute = async () => {
      const result = await onUpdateContractApplication({ contractId: Number(contract?.contractId), formData: formData });
      if (result) {
        await getContractApplication?.();
        setAbstractModalZ1({ isOpen: false });
        setAbstractModalZ2({ isOpen: false });
        setBaseModal({ isOpen: false });
      }
    };
  };

  // 이용신청서 모달 오픈
  const openApplicationModal = async () => {
    const applicationSummary = await getApplicationSummury({
      quotationNo: contract?.quotationList?.[0]?.quotationNo || "",
      contractApplication: contractApplication!,
    });

    // 수정 form 오픈
    const openApplicationEditModal = () => {
      const formData: ProductApplication = {
        formData: {
          richText: contractApplication?.applicationBody?.formData?.richText || "",
          attachmentConfig: {
            isRequired: contractApplication?.applicationBody?.formData?.attachmentConfig?.isRequired || false,
          },
        },
        attachments: contractApplication?.applicationBody.attachments || [],
      };

      // 이용신청서 수정 모달
      setAbstractModalZ2({
        isOpen: true,
        children: (
          <UsageApplicationForm
            fileList={contractApplication?.applicationBody.attachments || []}
            onClose={() => setAbstractModalZ2({ isOpen: false })}
            applicationSummary={applicationSummary}
            applicationFormData={formData}
            onSaveFormData={onUpdateCtApplication}
          />
        ),
      });
    };

    // 이용신청서 상세 모달
    setAbstractModalZ1({
      isOpen: true,
      children: (
        <UsageApplicationDetail //
          applicationValue={contractApplication?.applicationBody?.formData?.richText || ""}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onEdit={openApplicationEditModal}
          applicationSummary={applicationSummary}
          fileList={contractApplication?.applicationBody?.attachments || []}
        />
      ),
    });
  };

  return (
    <ArticleSection
      title={"신청정보"}
      children={
        <>
          <div className="flex-center">
            <BaseSection
              title={"신청자명"}
              children={
                <div className="flex-row flex-center-start w-100 font14">
                  <div
                    className={
                      contractApplication?.applicationBody?.applicantName && contractApplication?.applicationBody?.applicantName?.length > 15
                        ? ""
                        : "mr5"
                    }
                  >
                    {contractApplication?.applicationBody?.applicantName !== undefined &&
                    contractApplication?.applicationBody?.applicantName !== "" &&
                    contractApplication?.applicationBody?.applicantName?.length > 15 ? (
                      <div className={"flex-center-start"}>
                        <p>{contractApplication?.applicationBody?.applicantName!.substring(0, 15) + "..."}</p>
                        <BaseTooltip contents={""} tooltip={contractApplication?.applicationBody?.applicantName} touchIcon={"QUESTION"} />
                      </div>
                    ) : (
                      contractApplication?.applicationBody?.applicantName! || "-"
                    )}
                  </div>
                </div>
              }
            />
            <BaseSection
              title={"휴대폰"}
              value={
                contractApplication?.applicationBody?.applicantPhoneNumber
                  ? formatPhoneNumber(String(contractApplication?.applicationBody?.applicantPhoneNumber))
                  : "-"
              }
            />
          </div>
          <div className="flex-center">
            <BaseSection title={"이메일"} value={contractApplication?.applicationBody?.applicantEmail} />
            <BaseSection title={"회원번호"} value={contractApplication?.applicationBody?.applicantMemberNo || "-"} />
          </div>

          <div className="flex-center">
            <BaseSection
              title={"신청 기간"}
              children={
                detailData.product && detailData?.product?.productType === "TIME_COURT" ? (
                  <p>
                    {formatDateTimeRange(
                      contractApplication?.applicationBody?.useStartTime!,
                      contractApplication?.applicationBody?.useEndTime!,
                      "S2",
                    )}
                  </p>
                ) : (
                  <p>
                    {formatDateTimeRange(
                      contractApplication?.applicationBody?.useStartTime!,
                      contractApplication?.applicationBody?.useEndTime!,
                      "F2",
                    )}{" "}
                  </p>
                )
              }
            />
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p>이용 신청서</p>
              </div>
              <div className="contents-container__grid-contents">
                <div>
                  {contractApplication?.applicationBody.formData ? (
                    <div>
                      <BaseButton title="보기" className="color-white" onClick={openApplicationModal} />
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </section>
          </div>
        </>
      }
    />
  );
};

export default ContractorInfoDetail;
